import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { catchError, debounceTime, of, Subject, takeUntil } from 'rxjs';
import { Car, TransmissionType } from './manage-cars.interfaces';
import { EditCarDialogComponent } from '../dialogs/edit-car-dialog/edit-car-dialog.component';
import { CarService } from '../../services/car.service';
import { SnackbarService } from '../../services/snackbar.service';
import { mapAdminCarResponseToCar } from './manage-cars.mappers';

@Component({
  selector: 'app-manage-cars',
  templateUrl: './manage-cars.component.html',
  styleUrls: ['./manage-cars.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageCarsComponent implements OnInit, OnDestroy {

  // Table configuration
  displayedColumns: string[] = ['name', 'category', 'make', 'model', 'characteristics', 'image', 'actions'];
  dataSource = new MatTableDataSource<Car>([]);

  // Pagination
  pageSizeOptions = [10, 20, 50];
  pageSize = 10;
  pageIndex = 0;
  dataLength = 0;

  // Search
  searchTerm = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Loading state
  loading = false;

  constructor(
    private dialog: MatDialog,
    private carService: CarService,
    private snackbarService: SnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.pageIndex = 0;
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilter(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onPagination(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.cdr.markForCheck();

    const searchQuery = this.searchTerm.trim() || undefined;

    this.carService
      .getCars$(this.pageSize, this.pageSize * this.pageIndex, searchQuery)
      .pipe(
        catchError(() => {
          this.snackbarService.error('A járművek lekérése során hiba lépett fel!');
          this.loading = false;
          this.cdr.markForCheck();
          return of();
        }),
      )
      .subscribe((carBatch) => {
        this.dataSource.data = carBatch.items.map(mapAdminCarResponseToCar);
        this.dataLength = carBatch.totalCount;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  openEditDialog(car: Car): void {
    const dialogRef = this.dialog.open(EditCarDialogComponent, {
      data: { modelId: car.modelId },
      width: '90%',
      maxWidth: '1200px',
      panelClass: 'white-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.loadData();
      }
    });
  }

  // Template helper methods
  hasImageError(car: Car): boolean {
    return !car.imageAssetUrl || car.imageAssetUrl.trim() === '';
  }

  getTransmissionLabel(transmission: TransmissionType | undefined): string {
    if (!transmission) return '-';
    return transmission === 'MANUAL' ? 'Manuális' : 'Automata';
  }
}
